/** Safety latches (UX08, UX06): logout, tab close and acknowledgement release nothing and send no command. */
import type { IncidentRow } from "../contracts/views.ts";
import type { Scope } from "./dom.ts";
import { createStore, type Store } from "./store.ts";

export type Acknowledgements = Store<ReadonlySet<string>>;

/** The local record of acknowledged incidents; it is a display flag, never a server command. */
export function createAcknowledgements(): Acknowledgements {
  return createStore<ReadonlySet<string>>(new Set());
}

/** Acknowledge an incident locally; the latch it reports is untouched. */
export function acknowledge(store: Acknowledgements, incidentId: string): void {
  store.update((current) => new Set([...current, incidentId]));
}

export interface IncidentView {
  readonly row: IncidentRow;
  readonly acknowledged: boolean;
  readonly latchHeld: boolean;
}

/** An incident as displayed: acknowledged by the user or the server, the latch exactly as the server says. */
export function viewIncident(row: IncidentRow, local: ReadonlySet<string>): IncidentView {
  return {
    row,
    acknowledged: row.acknowledged || local.has(row.incidentId),
    latchHeld: row.safetyLatch === "ENGAGED",
  };
}

/** On `pagehide` only the transport is closed; nothing is sent and nothing is released. */
export function installPageHide(win: Window, scope: Scope, closeStreams: () => void): void {
  scope.listen(win, "pagehide", closeStreams);
}

export interface SensitiveState {
  readonly closeStreams: () => void;
  readonly abortRequests: () => void;
  readonly clearStores: readonly (() => void)[];
  readonly storage: Pick<Storage, "clear">;
}

/** Clear everything the page shows about an account: streams, in-flight reads, memory stores, session storage. */
export function clearSensitiveState(state: SensitiveState): void {
  state.closeStreams();
  state.abortRequests();
  for (const clear of state.clearStores) {
    clear();
  }
  state.storage.clear();
}

export interface LogoutDeps {
  readonly state: SensitiveState;
  /** `POST /auth/logout`: ends the application session; it is not a trading command. */
  readonly endSession: () => Promise<unknown>;
  readonly navigate: () => void;
}

/** Explicit logout: display state is cleared first, then the session is ended, then the page moves on. */
export async function logout(deps: LogoutDeps): Promise<void> {
  clearSensitiveState(deps.state);
  try {
    await deps.endSession();
  } finally {
    deps.navigate();
  }
}
